import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { BaseComponent } from '../../../base/base.component';
import { ButtonComponent } from '../../../core/buttons/button.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { AgenciesComponent } from './agencies.component';
import { DialogComponent } from '../../../core/panels/dialog.component';
import { InputComponent } from '../../../core/controls/input.component';
import { FilterSection, ICriteria } from '../edit-filter.component';
import { FilterCriteria } from '../../../../models/api/reports/filter-detail-response';
import { ReportsService } from '../../../../services/reports/reports.service';
import {
  AgencyFilter,
  AgencySearchInput,
} from '../../../../models/api/reports/agency-filter';

@Component({
  selector: 'app-agency-criteria',
  imports: [
    ReactiveFormsModule,
    CommonModule,
    ActionRowComponent,
    ButtonComponent,
    AgenciesComponent,
    DialogComponent,
    InputComponent,
  ],
  template: `
    <div class="tw-pt-0">
      <app-action-row>
        <app-button left1 label="Add" emphasis="Low" (click)="openDialog()" />
        <app-button
          left2
          label="Remove all"
          emphasis="Low"
          (click)="deleteAll()"
        />
      </app-action-row>

      <app-agencies
        [dataSource]="selectedAgencies"
        [showRemove]="true"
        (removed)="onAgencyRemoved($event)"
      />
    </div>
    <app-dialog
      #dialog
      [visible]="dialogVisible"
      [closable]="false"
      label="Add agencies"
    >
      <ng-template #content>
        <form [formGroup]="fg" class="tw-pt-2">
          <div class="tw-flex tw-flex-col md:tw-flex-row tw-gap-2 md:tw-gap-4">
            <app-input
              [control]="fg.controls.agencyNumber"
              label="Agency number"
              class="tw-w-full sm:tw-w-auto"
            />
            <app-input
              [control]="fg.controls.agencyName"
              label="Agency name"
              class="tw-w-full sm:tw-w-auto"
            />
            <app-input
              [control]="fg.controls.city"
              label="City"
              class="tw-w-full sm:tw-w-auto"
            />
            <app-input
              [control]="fg.controls.state"
              label="State"
              class="tw-w-full sm:tw-w-auto"
            />
          </div>
        </form>
        <app-action-row>
          <app-button right1 label="Search" (click)="search()" />
          <app-button
            right2
            emphasis="Low"
            label="Clear"
            (click)="clearSearch()"
          />
        </app-action-row>
        <app-agencies
          [dataSource]="agencySearchResults"
          [showRemove]="false"
          [showSelection]="true"
          paginatorDropdownAppendTo="self"
          (selected)="onSelectionChanged($event)"
        />
        <app-action-row>
          <app-button
            [disabled]="!addButtonEnabled"
            right1
            label="Add"
            (click)="addAgency()"
          />
          <app-button
            right2
            emphasis="Low"
            label="Close"
            (click)="closeDialog()"
          />
        </app-action-row>
      </ng-template>
    </app-dialog>
  `,
  styles: ``,
})
export class AgencyCriteriaComponent
  extends BaseComponent
  implements OnInit, ICriteria
{
  dialogVisible = false;
  addButtonEnabled = false;
  contractNumber: number;
  agencySearchResults: AgencyFilter[] = [];
  selectedAgencies: AgencyFilter[] = [];
  selectedRows: AgencyFilter[] = [];

  @Input({ required: true })
  section: FilterSection;

  private reports = inject(ReportsService);

  fg = new FormGroup({
    agencyNumber: new FormControl<string>(null),
    agencyName: new FormControl<string>(null),
    city: new FormControl<string>(null),
    state: new FormControl<string>(null),
  });

  constructor() {
    super();
  }

  async setAgencyFromCriteria(criteria: FilterCriteria[]) {
    const agencyNumbers = criteria
      .filter((c) => c.column === 'AGENCY_NUMBER')
      .flatMap((c) => c.values);

    if (!agencyNumbers.length) return;

    this.selectedAgencies = await this.api.filtersCriteria.getAgencies(
      this.contractNumber,
      agencyNumbers,
    );
    this.emitCount();
  }

  async ngOnInit() {
    this.section.criteria = this;
    const client = this.reports.selectedClient();

    if (client) {
      this.contractNumber = Number(client);
    }

    if (this.section.values?.length) {
      await this.setAgencyFromCriteria(this.section.values);
    }
  }

  valid(): boolean {
    return true;
  }

  openDialog() {
    this.dialogVisible = true;
  }

  onSelectionChanged(event: AgencyFilter[]) {
    this.selectedRows = event;
    this.addButtonEnabled = event.length > 0;
  }

  onAgencyRemoved(row: AgencyFilter) {
    this.selectedAgencies = this.selectedAgencies.filter(
      (agency) => agency.agencyNumber !== row.agencyNumber,
    );
    this.emitCount();
  }

  addAgency() {
    this.selectedAgencies = [...this.selectedAgencies, ...this.selectedRows];

    this.dialogVisible = false;
    this.addButtonEnabled = false;
    this.agencySearchResults = [];
    this.selectedRows = [];
    this.clearSearch();
    this.emitCount();
  }

  closeDialog() {
    this.dialogVisible = false;
    this.addButtonEnabled = false;
    this.agencySearchResults = [];
    this.selectedRows = [];
    this.clearSearch();
  }

  deleteAll() {
    this.selectedAgencies = [];
    this.section.count.set(0);
  }

  clearSearch() {
    this.fg.controls.agencyNumber.setValue(null);
    this.fg.controls.agencyName.setValue(null);
    this.fg.controls.city.setValue(null);
    this.fg.controls.state.setValue(null);
    this.agencySearchResults = [];
    this.selectedRows = [];
    this.addButtonEnabled = false;
  }

  async search() {
    const input: AgencySearchInput = {
      contractNumber: this.contractNumber,
      agencyNumber: this.fg.controls.agencyNumber.value,
      agencyName: this.fg.controls.agencyName.value,
      city: this.fg.controls.city.value,
      state: this.fg.controls.state.value,
    };

    const results = await this.api.filtersCriteria.searchAgencies(input);
    this.agencySearchResults = results.filter(
      (r) =>
        !this.selectedAgencies.some(
          (existing) => existing.agencyNumber === r.agencyNumber,
        ),
    );
  }

  private emitCount() {
    const count = this.selectedAgencies.length;
    this.section.count.set(count);
  }

  getCriteria(): FilterCriteria[] {
    return this.selectedAgencies.map(
      (agency) =>
        ({
          tableAlias: 'agency_master',
          column: 'AGENCY_NUMBER',
          displayName: '',
          operator: 'EQUALS',
          values: [agency.agencyNumber],
          lineOfBusiness: '',
          dataType: '',
          isClaimLab: false,
          formattedOperator: '',
          formattedValue: '',
          filterSection: 'Agency',
          filterSubsection: '',
        }) as FilterCriteria,
    );
  }

  clearCriteria(): void {
    this.selectedAgencies = [];
    this.agencySearchResults = [];
    this.selectedRows = [];
    this.addButtonEnabled = false;
    this.section.count.set(0);
  }
}
